import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import html2pdf from 'html2pdf.js';
import { useAuth } from '../contexts/AuthContext.tsx';
import { collection, addDoc, updateDoc, doc, Timestamp, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { Invoice, InvoiceLineItem, INVOICE_CONSTANTS } from '../types/invoice.ts';
import { Contractor } from '../types/contractor.ts';
import { invoiceNumberingService } from '../services/invoiceNumberingService.ts';

interface JobInvoiceProps {
  jobData: any;
  invoiceNumber: string;
  useBtcPrefix?: boolean;
  useRelPrefix?: boolean;
  businessInfo: any;
  onSaveSuccess?: (invoiceNumber: string) => void;
  existingInvoiceId?: string; // Add this for editing existing invoices
  // Called whenever the working copy of this invoice's data changes (hours,
  // rates, expenses, etc. edited in Edit Invoice mode) so a parent showing
  // a summary/total across multiple invoices can stay in sync live instead
  // of only reflecting what was last saved to Firestore.
  onDataChange?: (invoiceNumber: string, data: any) => void;
}

const logoUrl = '/logo.png';

const JobInvoice: React.FC<JobInvoiceProps> = ({
  jobData,
  invoiceNumber,
  useBtcPrefix = false,
  useRelPrefix = false,
  businessInfo,
  onSaveSuccess,
  existingInvoiceId,
  onDataChange
}) => {
  const { currentUser } = useAuth();
  const componentRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState<any>(null);
  // Manual override for the invoice number, editable in Edit Invoice mode.
  // null means "use the auto-generated number below" (default behavior).
  const [editedInvoiceNumber, setEditedInvoiceNumber] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [contractorsLoading, setContractorsLoading] = useState(true);

  // Auto-generated invoice number (with BTC-/REL- prefix applied), used
  // unless the user has manually overridden it via the Invoice # field.
  const defaultFormattedInvoiceNumber = useMemo(() => {
    if (useBtcPrefix && !invoiceNumber.includes('BTC-')) return `BTC-${invoiceNumber}`;
    if (useRelPrefix && !invoiceNumber.includes('REL-')) return `REL-${invoiceNumber}`;
    return invoiceNumber;
  }, [invoiceNumber, useBtcPrefix, useRelPrefix]);
  const displayInvoiceNumber = editedInvoiceNumber !== null && editedInvoiceNumber.trim() !== ''
    ? editedInvoiceNumber
    : defaultFormattedInvoiceNumber;

  // Load contractors for client selection
  const loadContractors = useCallback(async () => {
    try {
      setContractorsLoading(true);
      const contractorsRef = collection(db, 'contractors');
      const contractorsQuery = query(contractorsRef, where('status', '==', 'active'), orderBy('companyName'));
      const snapshot = await getDocs(contractorsQuery);
      
      const contractorData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        licenseExpires: doc.data().licenseExpires?.toDate(),
        insuranceExpires: doc.data().insuranceExpires?.toDate(),
        bondExpires: doc.data().bondExpires?.toDate(),
      })) as Contractor[];

      setContractors(contractorData);
    } catch (err) {
      console.error('Error loading contractors:', err);
    } finally {
      setContractorsLoading(false);
    }
  }, []);

  // Initialize editedData with the original jobData
  useEffect(() => {
    setEditedData(JSON.parse(JSON.stringify(jobData)));
    loadContractors();
    
    // Initialize date range if using BTC prefix
    if (useBtcPrefix && jobData.weekEnding) {
      try {
        // Try to parse the week ending date
        const weekEndingDate = new Date(jobData.weekEnding);
        if (!isNaN(weekEndingDate.getTime())) {
          // Set end date to week ending date
          setEndDate(weekEndingDate.toISOString().split('T')[0]);
          
          // Set start date to 6 days before end date (for a week)
          const startDateObj = new Date(weekEndingDate);
          startDateObj.setDate(startDateObj.getDate() - 6);
          setStartDate(startDateObj.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error parsing week ending date:', error);
      }
    }
  }, [jobData, useBtcPrefix, loadContractors]);

  // Bubble edits up to the parent so a batch summary derived from parent
  // state reflects unsaved edits immediately, not just what's been saved
  // to Firestore. Content-compared against jobData (rather than firing on
  // every editedData change unconditionally) so that when the parent
  // applies the update and a fresh jobData prop flows back down, the reset
  // in the effect above doesn't bounce back into another parent update -
  // it settles once editedData and jobData agree.
  useEffect(() => {
    if (!editedData || !onDataChange) return;
    if (JSON.stringify(editedData) === JSON.stringify(jobData)) return;
    onDataChange(invoiceNumber, editedData);
  }, [editedData, jobData, invoiceNumber, onDataChange]);

  // Set selected client when contractors and jobData are loaded
  useEffect(() => {
    if (contractors.length > 0 && jobData.clientName) {
      const matchingContractor = contractors.find(c => c.companyName === jobData.clientName);
      if (matchingContractor) {
        setSelectedClient(matchingContractor.id);
      } else {
        // Fallback to first contractor if no match found
        setSelectedClient(contractors[0]?.id || '');
      }
    }
  }, [contractors, jobData.clientName]);

  const handleDownload = () => {
    const printContent = componentRef.current;
    if (!printContent) {
      console.error('Print content not found');
      return;
    }

    const fileName = `INV#${displayInvoiceNumber} ${jobData.jobNumber} ${jobData.jobName}.pdf`;
    const opt = {
      margin: 10,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
    };

    html2pdf().from(printContent).set(opt).save();
  };

  // Helper function to get contractor payment terms
  const getContractorPaymentTerms = (): string => {
    // First, check if payment terms are directly available in jobData (from batch processing)
    if ((jobData as any).paymentTerms) {
      return (jobData as any).paymentTerms;
    }
    
    // Fallback to contractor lookup
    const matchingContractor = contractors.find(c => c.companyName === jobData.clientName);
    if (matchingContractor?.billing?.paymentTerms) {
      const terms = matchingContractor.billing.paymentTerms;
      switch (terms) {
        case 'net15': return 'Net 15 Days';
        case 'net30': return 'Net 30 Days';
        case 'net45': return 'Net 45 Days';
        case 'immediate': return 'Due Immediately';
        case 'weekly': return 'Weekly';
        case 'biweekly': return 'Bi-weekly';
        default: return businessInfo.paymentTerms || 'Net 30';
      }
    }
    return businessInfo.paymentTerms || 'Net 30';
  };

  // Use editedData if available, otherwise use jobData
  const dataToCalculate = editedData || jobData;

  const totalsResult = useMemo(() => {
    let totalRegularHours = 0;
    let totalOvertimeHours = 0;
    let totalAmount = 0;
    let totalExpenses = 0;
    let activityTotals: any = {};
    let hasActivities = false;
    let hasExpenses = false;

    Object.entries(dataToCalculate.employees).forEach(([employeeName, employee]: [string, any]) => {
      // Check if employee has activities or direct hours
      if (employee.activities && Object.keys(employee.activities).length > 0) {
        hasActivities = true;
        Object.entries(employee.activities).forEach(([activity, activityData]: [string, any]) => {
          // Round hours to 2 decimal places for consistency
          totalRegularHours += (activityData.regularHours || 0);
          totalOvertimeHours += (activityData.overtimeHours || 0);
          const activityTotal = (activityData.regularTotal + activityData.overtimeTotal);
          totalAmount += activityTotal;

          if (!activityTotals[activity]) {
            activityTotals[activity] = {
              regularHours: 0,
              overtimeHours: 0,
              total: 0
            };
          }
          // Round activity hours to 2 decimal places for consistency
          activityTotals[activity].regularHours += (activityData.regularHours || 0);
          activityTotals[activity].overtimeHours += (activityData.overtimeHours || 0);
          activityTotals[activity].total += (activityData.regularTotal + activityData.overtimeTotal);
        });
      } else if (employee.regularHours !== undefined || employee.overtimeHours !== undefined) {
        // If employee has direct hours (no activities)
        totalRegularHours += (employee.regularHours || 0);
        totalOvertimeHours += (employee.overtimeHours || 0);
        // Ensure OT rate is properly rounded for totals calculation too
        const roundedOTRate = Math.round((employee.overtimeRate || 0) * 100) / 100;
        const regularTotal = (employee.regularHours || 0) * (employee.regularRate || 0);
        const overtimeTotal = (employee.overtimeHours || 0) * roundedOTRate;
        totalAmount += regularTotal + overtimeTotal;
      }

      // Calculate expenses
      if (employee.expenses && employee.expenses.length > 0) {
        hasExpenses = true;
        employee.expenses.forEach((expense: any) => {
          const expenseAmount = parseFloat(expense.amount || 0);
          totalExpenses += expenseAmount;
          totalAmount += expenseAmount;
        });
      }
    });

    const sortedActivityTotals = Object.entries(activityTotals).sort((a: any, b: any) => b[1].total - a[1].total);

    return {
      totalRegularHours,
      totalOvertimeHours,
      totalAmount,
      totalExpenses,
      sortedActivityTotals,
      hasActivities,
      hasExpenses
    };
  }, [dataToCalculate]);

  const {
    totalRegularHours,
    totalOvertimeHours,
    totalAmount,
    totalExpenses,
    sortedActivityTotals,
    hasActivities,
    hasExpenses
  } = totalsResult;

  const handleSave = async () => {
    if (!currentUser) {
      setSaveError('User not authenticated');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Use editedData if available, otherwise use jobData
      const dataToSave = editedData || jobData;
      
      // Use the manually-edited invoice number if one was set, otherwise
      // fall back to the auto-generated (prefixed) number.
      const formattedInvoiceNumber = displayInvoiceNumber;

      // Create line items from the job data
      const lineItems: InvoiceLineItem[] = [];
      let lineItemId = 1;

      Object.entries(dataToSave.employees).forEach(([employeeName, employeeData]: [string, any]) => {
        // Handle activities
        if (employeeData.activities && Object.keys(employeeData.activities).length > 0) {
          Object.entries(employeeData.activities).forEach(([activity, activityData]: [string, any]) => {
            const regularTotal = (activityData.regularHours || 0) * (activityData.regularRate || 0);
            const overtimeTotal = (activityData.overtimeHours || 0) * (activityData.overtimeRate || 0);

            lineItems.push({
              id: `line-${lineItemId++}`,
              employeeName,
              activityDescription: activity !== 'undefined' ? activity : undefined,
              regularHours: activityData.regularHours || 0,
              overtimeHours: activityData.overtimeHours || 0,
              totalHours: (activityData.regularHours || 0) + (activityData.overtimeHours || 0),
              regularRate: activityData.regularRate || 0,
              overtimeRate: activityData.overtimeRate || 0,
              regularTotal,
              overtimeTotal,
              lineTotal: regularTotal + overtimeTotal,
              isExpense: false
            });
          });
        } else if (employeeData.regularHours !== undefined || employeeData.overtimeHours !== undefined) {
          // Handle direct hours (no activities)
          const regularTotal = parseFloat(((employeeData.regularHours || 0) * (employeeData.regularRate || 0)).toFixed(2));
          const overtimeTotal = parseFloat(((employeeData.overtimeHours || 0) * (employeeData.overtimeRate || 0)).toFixed(2));
          
          lineItems.push({
            id: `line-${lineItemId++}`,
            employeeName,
            regularHours: employeeData.regularHours || 0,
            overtimeHours: employeeData.overtimeHours || 0,
            totalHours: (employeeData.regularHours || 0) + (employeeData.overtimeHours || 0),
            regularRate: employeeData.regularRate || 0,
            overtimeRate: employeeData.overtimeRate || 0,
            regularTotal,
            overtimeTotal,
            lineTotal: regularTotal + overtimeTotal,
            isExpense: false
          });
        }

        // Handle expenses
        if (employeeData.expenses && employeeData.expenses.length > 0) {
          employeeData.expenses.forEach((expense: any) => {
            lineItems.push({
              id: `line-${lineItemId++}`,
              employeeName,
              regularHours: 0,
              overtimeHours: 0,
              totalHours: 0,
              regularRate: 0,
              overtimeRate: 0,
              regularTotal: 0,
              overtimeTotal: 0,
              lineTotal: expense.amount || 0,
              isExpense: true,
              expenseDescription: expense.description || '',
              expenseAmount: expense.amount || 0
            });
          });
        }
      });

      // Calculate totals
      const subtotal = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);
      const taxRate = 0; // No tax for now
      const taxAmount = subtotal * taxRate;
      const totalAmount = subtotal + taxAmount;
      const totalExpenses = lineItems.filter(item => item.isExpense).reduce((sum, item) => sum + item.lineTotal, 0);

      // Parse dates
      const invoiceDateObj = new Date(invoiceDate);
      const dueDateObj = new Date(invoiceDateObj);
      dueDateObj.setDate(dueDateObj.getDate() + INVOICE_CONSTANTS.DEFAULT_PAYMENT_TERMS);

      // Create the invoice object
      const invoiceData: Omit<Invoice, 'id'> = {
        invoiceNumber: formattedInvoiceNumber,
        jobId: dataToSave.jobNumber || '', // Using jobNumber as jobId for now
        jobNumber: dataToSave.jobNumber || '',
        jobName: dataToSave.jobName || '',
        clientId: selectedClient || dataToSave.contractorId || undefined,
        clientName: dataToSave.clientName || INVOICE_CONSTANTS.CLIENTS.CEC.name,
        clientAddress: dataToSave.clientAddress || INVOICE_CONSTANTS.CLIENTS.CEC.address,
        clientCity: dataToSave.clientCity || INVOICE_CONSTANTS.CLIENTS.CEC.city,
        clientState: dataToSave.clientState || INVOICE_CONSTANTS.CLIENTS.CEC.state,
        clientZip: dataToSave.clientZip || INVOICE_CONSTANTS.CLIENTS.CEC.zip,
        contractorId: selectedClient || dataToSave.contractorId || undefined,
        contractorName: dataToSave.contractorName || undefined,
        invoiceDate: invoiceDateObj,
        dueDate: dueDateObj,
        weekEnding: useBtcPrefix && startDate && endDate ? `${startDate} to ${endDate}` : (dataToSave.weekEnding || ''),
        periodStart: startDate ? new Date(startDate) : undefined,
        periodEnd: endDate ? new Date(endDate) : undefined,
        lineItems,
        totalRegularHours,
        totalOvertimeHours,
        totalHours: totalRegularHours + totalOvertimeHours,
        subtotal,
        taxRate,
        taxAmount,
        totalAmount,
        totalExpenses,
        status: 'draft' as const,
        useBtcPrefix: useBtcPrefix || false,
        useRelPrefix: useRelPrefix || false,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUser.uid,
        timesheetIds: [] // Empty for now, could be populated if we track timesheet relationships
      };

      // Prepare data for Firebase (remove undefined values)
      const firebaseData = {
        ...invoiceData,
        invoiceDate: Timestamp.fromDate(invoiceData.invoiceDate),
        dueDate: Timestamp.fromDate(invoiceData.dueDate),
        periodStart: invoiceData.periodStart ? Timestamp.fromDate(invoiceData.periodStart) : null,
        periodEnd: invoiceData.periodEnd ? Timestamp.fromDate(invoiceData.periodEnd) : null,
        createdAt: Timestamp.fromDate(invoiceData.createdAt),
        updatedAt: Timestamp.fromDate(invoiceData.updatedAt)
      };

      // Remove undefined fields as Firebase doesn't support them
      Object.keys(firebaseData).forEach(key => {
        if (firebaseData[key as keyof typeof firebaseData] === undefined) {
          delete firebaseData[key as keyof typeof firebaseData];
        }
      });

      // Save to Firebase - either update existing or create new
      if (existingInvoiceId) {
        // Update existing invoice
        await updateDoc(doc(db, 'invoices', existingInvoiceId), {
          ...firebaseData,
          updatedAt: Timestamp.now() // Update timestamp
        });
        console.log('Invoice updated with ID:', existingInvoiceId);
      } else {
        // Create new invoice - reserve a proper invoice number now
        console.log('Saving new invoice, reserving invoice number:', formattedInvoiceNumber);
        
        // Determine company prefix based on invoice number format
        let companyPrefix = 'CEC';
        if (formattedInvoiceNumber.includes('-')) {
          const parts = formattedInvoiceNumber.split('-');
          if (parts.length >= 2) {
            companyPrefix = parts[0];
          }
        }
        
        try {
          // Reserve the invoice number in the numbering system
          console.log(`Reserving invoice number for ${companyPrefix}...`);
          const reservedInvoice = await invoiceNumberingService.reserveInvoiceNumber(
            companyPrefix,
            invoiceData.jobNumber || '',
            invoiceData.jobName || ''
          );
          console.log(`Reserved invoice number: ${reservedInvoice.fullInvoiceNumber}`);
          
          // Update the invoice data with the actual reserved number
          const actualInvoiceNumber = reservedInvoice.fullInvoiceNumber;
          firebaseData.invoiceNumber = actualInvoiceNumber;
          firebaseData.number = actualInvoiceNumber; // Also set 'number' field for compatibility
          
          console.log(`Using reserved invoice number: ${actualInvoiceNumber}`);
        } catch (reservationError) {
          console.error('Error reserving invoice number:', reservationError);
          console.log('Continuing with original number:', formattedInvoiceNumber);
          // Continue with the original number if reservation fails
        }
        
        // Create new invoice
        const docRef = await addDoc(collection(db, 'invoices'), firebaseData);
        console.log('Invoice created with ID:', docRef.id);
      }
      
      if (onSaveSuccess) {
        // Use the actual saved invoice number (which might be the reserved number)
        const finalInvoiceNumber = firebaseData.invoiceNumber || formattedInvoiceNumber;
        onSaveSuccess(finalInvoiceNumber);
      }
      setEditMode(false);
    } catch (error) {
      console.error('Error saving invoice:', error);
      setSaveError(`Failed to save invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndPrint = async () => {
    await handleSave();
    handleDownload();
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleInputChange = (employee: string, activity: string, field: string, value: any) => {
    setEditedData((prevData: any) => {
      const newData = { ...prevData };
      
      // Ensure the employee and activity exist
      if (!newData.employees[employee]) {
        newData.employees[employee] = { activities: {} };
      }
      if (!newData.employees[employee].activities[activity]) {
        newData.employees[employee].activities[activity] = {
          activityCode: '',
          activityDescription: '',
          regularHours: 0,
          overtimeHours: 0,
          regularRate: 0,
          overtimeRate: 0,
          regularTotal: 0,
          overtimeTotal: 0
        };
      }
      
      // Update the field
      newData.employees[employee].activities[activity][field] = value;
      
      // Recalculate totals if hours or rates changed
      if (['regularHours', 'regularRate', 'overtimeHours', 'overtimeRate'].includes(field)) {
        const activityObj = newData.employees[employee].activities[activity];
        activityObj.regularTotal = activityObj.regularHours * activityObj.regularRate;
        activityObj.overtimeTotal = activityObj.overtimeHours * activityObj.overtimeRate;
      }
      
      return newData;
    });
  };

  const handleAddExpense = (employee: string) => {
    setEditedData((prevData: any) => {
      const newData = { ...prevData };
      
      // Ensure the employee exists and has an expenses array
      if (!newData.employees[employee]) {
        newData.employees[employee] = { activities: {}, expenses: [] };
      }
      if (!newData.employees[employee].expenses) {
        newData.employees[employee].expenses = [];
      }
      
      // Add a new expense
      newData.employees[employee].expenses.push({
        description: 'Per Diem',
        amount: 0
      });
      
      return newData;
    });
  };

  const handleExpenseChange = (employee: string, index: number, field: string, value: any) => {
    setEditedData((prevData: any) => {
      const newData = { ...prevData };
      
      // Ensure the employee exists and has an expenses array
      if (!newData.employees[employee]) {
        newData.employees[employee] = { activities: {}, expenses: [] };
      }
      if (!newData.employees[employee].expenses) {
        newData.employees[employee].expenses = [];
      }
      
      // Update the expense field
      if (newData.employees[employee].expenses[index]) {
        newData.employees[employee].expenses[index][field] = value;
      }
      
      return newData;
    });
  };

  const handleRemoveExpense = (employee: string, index: number) => {
    setEditedData((prevData: any) => {
      const newData = { ...prevData };
      
      // Remove the expense at the specified index
      if (newData.employees[employee] && 
          newData.employees[employee].expenses && 
          newData.employees[employee].expenses.length > index) {
        newData.employees[employee].expenses.splice(index, 1);
      }
      
      return newData;
    });
  };

  const handleJobDataChange = (field: string, value: any) => {
    setEditedData((prevData: any) => {
      const newData = { ...prevData };
      
      // Handle nested properties (e.g., 'employees.employeeName.regularHours')
      if (field.includes('.')) {
        const parts = field.split('.');
        let current = newData;
        
        // Navigate to the nested property
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
        
        // Set the value
        current[parts[parts.length - 1]] = value;
        
        return newData;
      } else {
        // Handle top-level properties
        return {
          ...prevData,
          [field]: value
        };
      }
    });
  };

  const handleClientChange = (contractorId: string) => {
    setSelectedClient(contractorId);
    const contractor = contractors.find(c => c.id === contractorId);
    
    if (contractor) {
      setEditedData((prevData: any) => ({
        ...prevData,
        clientName: contractor.companyName,
        clientAddress: contractor.address.street,
        clientCity: contractor.address.city,
        clientState: contractor.address.state,
        clientZip: contractor.address.zipCode
      }));
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };

  const sortActivities = (activities: any) => {
    return Object.entries(activities).sort((a: any, b: any) => b[1].regularHours - a[1].regularHours);
  };

  // Use editedData for rendering if available, otherwise use jobData
  const dataToRender = editedData || jobData;

  return (
    <div className="invoice-container max-w-6xl mx-auto p-8 bg-background text-foreground shadow-lg rounded-lg print:w-full print:max-w-none">
      <div className="no-print mb-4 flex flex-wrap gap-2">
        <Button onClick={handleDownload} variant="outline">Print</Button>
        <Button onClick={handleSave} disabled={isSaving}>Save</Button>
        <Button onClick={handleSaveAndPrint} disabled={isSaving}>Save & Print</Button>
        <Button onClick={handleEditToggle} variant={editMode ? "destructive" : "secondary"}>
          {editMode ? "Cancel Edit" : "Edit Invoice"}
        </Button>
      </div>
      
      {saveError && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md">
          Error: {saveError}
        </div>
      )}
      
      <div ref={componentRef} className="p-8 bg-background text-foreground">
        <div className="flex justify-between items-start mb-8">
          <div>
            <img src={logoUrl} alt="Company Logo" className="h-24 mb-4" />
            <h1 className="text-3xl font-bold">{businessInfo.name}</h1>
            <p>{businessInfo.address}</p>
            <p>{businessInfo.city}, {businessInfo.state} {businessInfo.zip}</p>
            <p>Phone: {businessInfo.phone}</p>
            <p>Email: {businessInfo.email}</p>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold mb-2">Invoice</h2>
            <p>
              <strong>Invoice #:</strong>
              {editMode ? (
                <input
                  type="text"
                  value={editedInvoiceNumber !== null ? editedInvoiceNumber : defaultFormattedInvoiceNumber}
                  onChange={(e) => setEditedInvoiceNumber(e.target.value)}
                  className="ml-2 border border-gray-300 rounded px-2 py-1"
                />
              ) : (
                <span> {displayInvoiceNumber}</span>
              )}
            </p>
            <p>
              <strong>Job Name:</strong> 
              {editMode ? (
                <input 
                  type="text" 
                  value={dataToRender.jobName} 
                  onChange={(e) => handleJobDataChange('jobName', e.target.value)}
                  className="ml-2 border border-gray-300 rounded px-2 py-1"
                />
              ) : (
                <span> {dataToRender.jobName}</span>
              )}
            </p>
            <p>
              <strong>Job Number:</strong> 
              {editMode ? (
                <input 
                  type="text" 
                  value={dataToRender.jobNumber} 
                  onChange={(e) => handleJobDataChange('jobNumber', e.target.value)}
                  className="ml-2 border border-gray-300 rounded px-2 py-1"
                />
              ) : (
                <span> {dataToRender.jobNumber}</span>
              )}
            </p>
            <p>
              <strong>{useBtcPrefix ? "Date Range:" : "Week Ending Date:"}</strong> 
              {editMode ? (
                useBtcPrefix ? (
                  <span className="ml-2">
                    <input 
                      type="date" 
                      value={startDate} 
                      onChange={(e) => setStartDate(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 mr-2"
                    />
                    to
                    <input 
                      type="date" 
                      value={endDate} 
                      onChange={(e) => setEndDate(e.target.value)}
                      className="ml-2 border border-gray-300 rounded px-2 py-1"
                    />
                  </span>
                ) : (
                  <input 
                    type="text" 
                    value={dataToRender.weekEnding} 
                    onChange={(e) => handleJobDataChange('weekEnding', e.target.value)}
                    className="ml-2 border border-gray-300 rounded px-2 py-1"
                  />
                )
              ) : (
                <span> {useBtcPrefix && startDate && endDate ? `${startDate} to ${endDate}` : dataToRender.weekEnding}</span>
              )}
            </p>
            <p>
              <strong>Invoice Date:</strong> 
              {editMode ? (
                <input 
                  type="date" 
                  value={invoiceDate} 
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="ml-2 border border-gray-300 rounded px-2 py-1"
                />
              ) : (
                <span> {invoiceDate}</span>
              )}
            </p>
            {editMode && (
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">Internal Client Assignment:</p>
                <select 
                  value={selectedClient} 
                  onChange={(e) => handleClientChange(e.target.value)}
                  disabled={contractorsLoading}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                >
                  <option value="">Select contractor...</option>
                  {contractors.map(contractor => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.companyName}
                    </option>
                  ))}
                </select>
                {contractorsLoading && (
                  <p className="text-xs text-gray-500 mt-1">Loading contractors...</p>
                )}
                {!contractorsLoading && dataToRender.clientName && (
                  <div className="mt-1 text-xs text-gray-600">
                    <p>Selected: {dataToRender.clientName}</p>
                    <p>{dataToRender.clientAddress}, {dataToRender.clientCity}, {dataToRender.clientState} {dataToRender.clientZip}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <Table className="print-table">
          <TableHeader>
            <TableRow className="bg-muted">
              <TableHead className="w-1/6">Employee</TableHead>
              {hasActivities && <TableHead className="w-1/4">Activity</TableHead>}
              <TableHead className="text-right w-1/12">Regular Hours</TableHead>
              <TableHead className="text-right w-1/12">OT Hours</TableHead>
              <TableHead className="text-right w-1/12">Regular Rate</TableHead>
              <TableHead className="text-right w-1/12">OT Rate</TableHead>
              <TableHead className="text-right w-1/6">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(dataToRender.employees).map(([employee, data]: [string, any], employeeIndex) => {
              // Check if this employee has activities
              const employeeHasActivities = data.activities && Object.keys(data.activities).length > 0;
              
              if (employeeHasActivities) {
                // Render employee with activities
                return (
                  <React.Fragment key={employee}>
                    {sortActivities(data.activities).map(([activity, activityData]: [string, any], activityIndex) => (
                      <TableRow key={`${employee}-${activity}`} className={activityIndex % 2 === 0 ? 'bg-background' : 'bg-muted/50'}>
                        <TableCell>{activityIndex === 0 ? employee : ''}</TableCell>
                        {hasActivities && <TableCell>{activity === 'undefined' ? '' : activity}</TableCell>}
                        <TableCell className="text-right">
                          {editMode ? (
                            <input 
                              type="number" 
                              value={activityData.regularHours} 
                              onChange={(e) => handleInputChange(employee, activity, 'regularHours', parseFloat(e.target.value) || 0)}
                              className="w-full text-right border border-gray-300 rounded px-2 py-1"
                              step="0.01"
                            />
                          ) : (
                            formatNumber(activityData.regularHours)
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {editMode ? (
                            <input 
                              type="number" 
                              value={activityData.overtimeHours} 
                              onChange={(e) => handleInputChange(employee, activity, 'overtimeHours', parseFloat(e.target.value) || 0)}
                              className="w-full text-right border border-gray-300 rounded px-2 py-1"
                              step="0.01"
                            />
                          ) : (
                            formatNumber(activityData.overtimeHours)
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {editMode ? (
                            <input 
                              type="number" 
                              value={activityData.regularRate} 
                              onChange={(e) => handleInputChange(employee, activity, 'regularRate', parseFloat(e.target.value) || 0)}
                              className="w-full text-right border border-gray-300 rounded px-2 py-1"
                              step="0.01"
                            />
                          ) : (
                            formatCurrency(activityData.regularRate)
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {editMode ? (
                            <input 
                              type="number" 
                              value={activityData.overtimeRate} 
                              onChange={(e) => handleInputChange(employee, activity, 'overtimeRate', parseFloat(e.target.value) || 0)}
                              className="w-full text-right border border-gray-300 rounded px-2 py-1"
                              step="0.01"
                            />
                          ) : (
                            formatCurrency(activityData.overtimeRate)
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(activityData.regularTotal + activityData.overtimeTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              } else if (data.regularHours !== undefined || data.overtimeHours !== undefined) {
                // Render employee without activities (direct hours)
                // Ensure OT rate is properly rounded
                const roundedOTRate = Math.round((data.overtimeRate || 0) * 100) / 100;
                const regularTotal = (data.regularHours || 0) * (data.regularRate || 0);
                const overtimeTotal = (data.overtimeHours || 0) * roundedOTRate;
                const total = regularTotal + overtimeTotal;

                return (
                  <TableRow key={employee} className={employeeIndex % 2 === 0 ? 'bg-background' : 'bg-muted/50'}>
                    <TableCell>{employee}</TableCell>
                    {hasActivities && <TableCell></TableCell>}
                    <TableCell className="text-right">
                      {editMode ? (
                        <input 
                          type="number" 
                          value={data.regularHours || 0} 
                          onChange={(e) => handleJobDataChange(`employees.${employee}.regularHours`, parseFloat(e.target.value) || 0)}
                          className="w-full text-right border border-gray-300 rounded px-2 py-1"
                          step="0.01"
                        />
                      ) : (
                        formatNumber(data.regularHours || 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editMode ? (
                        <input 
                          type="number" 
                          value={data.overtimeHours || 0} 
                          onChange={(e) => handleJobDataChange(`employees.${employee}.overtimeHours`, parseFloat(e.target.value) || 0)}
                          className="w-full text-right border border-gray-300 rounded px-2 py-1"
                          step="0.01"
                        />
                      ) : (
                        formatNumber(data.overtimeHours || 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editMode ? (
                        <input 
                          type="number" 
                          value={data.regularRate || 0} 
                          onChange={(e) => handleJobDataChange(`employees.${employee}.regularRate`, parseFloat(e.target.value) || 0)}
                          className="w-full text-right border border-gray-300 rounded px-2 py-1"
                          step="0.01"
                        />
                      ) : (
                        formatCurrency(data.regularRate || 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editMode ? (
                        <input 
                          type="number" 
                          value={data.overtimeRate || 0} 
                          onChange={(e) => handleJobDataChange(`employees.${employee}.overtimeRate`, parseFloat(e.target.value) || 0)}
                          className="w-full text-right border border-gray-300 rounded px-2 py-1"
                          step="0.01"
                        />
                      ) : (
                        formatCurrency(roundedOTRate)
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(total)}</TableCell>
                  </TableRow>
                );
              } else {
                return null; // Skip employees with no data
              }
            })}
          </TableBody>
        </Table>

        {/* Only show activity totals if there are activities mapped */}
        {hasActivities && sortedActivityTotals.length > 0 && (
          <div className="mt-8 activity-section">
            <h3 className="text-xl font-bold mb-2">Activity Totals</h3>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted">
                  <TableHead>Activity</TableHead>
                  <TableHead className="text-right">Regular Hours</TableHead>
                  <TableHead className="text-right">OT Hours</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedActivityTotals.map(([activity, totals]: [string, any], index) => (
                  <TableRow key={activity} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/50'}>
                    <TableCell>{activity}</TableCell>
                    <TableCell className="text-right">{formatNumber(totals.regularHours)}</TableCell>
                    <TableCell className="text-right">{formatNumber(totals.overtimeHours)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(totals.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Expenses Section - Only show if there are expenses or in edit mode */}
        {(hasExpenses || editMode) && (
          <div className="mt-8 expenses-section">
            <h3 className="text-xl font-bold mb-2">Expenses</h3>
            {Object.entries(dataToRender.employees).map(([employee, data]: [string, any], employeeIndex) => {
              // Skip employees with no expenses if not in edit mode
              if (!editMode && (!data.expenses || data.expenses.length === 0)) {
                return null;
              }
              
              return (
                <div key={`expenses-${employee}`} className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-lg font-semibold">{employee}</h4>
                    {editMode && (
                      <Button 
                        onClick={() => handleAddExpense(employee)} 
                        variant="outline" 
                        className="text-sm py-1"
                      >
                        Add Expense
                      </Button>
                    )}
                  </div>
                  
                  {/* Display message if no expenses */}
                  {(!data.expenses || data.expenses.length === 0) && editMode && (
                    <p className="text-gray-500 italic">No expenses added yet.</p>
                  )}
                  
                  {/* Expenses Table */}
                  {data.expenses && data.expenses.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted">
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          {editMode && <TableHead className="w-24"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.expenses.map((expense: any, index: number) => (
                          <TableRow key={`${employee}-expense-${index}`} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/50'}>
                            <TableCell>
                              {editMode ? (
                                <input 
                                  type="text" 
                                  value={expense.description || ''} 
                                  onChange={(e) => handleExpenseChange(employee, index, 'description', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                />
                              ) : (
                                expense.description || ''
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {editMode ? (
                                <input 
                                  type="number" 
                                  value={expense.amount || 0} 
                                  onChange={(e) => handleExpenseChange(employee, index, 'amount', parseFloat(e.target.value) || 0)}
                                  className="w-full text-right border border-gray-300 rounded px-2 py-1"
                                  step="0.01"
                                />
                              ) : (
                                formatCurrency(expense.amount || 0)
                              )}
                            </TableCell>
                            {editMode && (
                              <TableCell>
                                <button 
                                  onClick={() => handleRemoveExpense(employee, index)}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="invoice-footer-section" style={{ pageBreakInside: 'avoid', minHeight: '200px', marginTop: '2rem' }}>
          <div className="text-right mb-6">
            <p className="text-lg font-semibold">Total Regular Hours: {formatNumber(totalRegularHours)}</p>
            <p className="text-lg font-semibold">Total Overtime Hours: {formatNumber(totalOvertimeHours)}</p>
            {totalExpenses > 0 && (
              <p className="text-lg font-semibold">Total Expenses: {formatCurrency(totalExpenses)}</p>
            )}
            <p className="text-2xl font-bold mt-2" style={{ pageBreakInside: 'avoid', pageBreakBefore: 'avoid' }}>
              Total Amount: {formatCurrency(totalAmount)}
            </p>
          </div>

          <div className="text-sm mb-6">
            <p><strong>Payment Terms:</strong> {getContractorPaymentTerms()}</p>
            <p><strong>Note:</strong> The pay shown is based on a burdened rate.</p>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>{businessInfo.name} | {businessInfo.address}, {businessInfo.city}, {businessInfo.state} {businessInfo.zip}</p>
            <p>Phone: {businessInfo.phone} | Email: {businessInfo.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Memoized so saving/editing one invoice in a large batch doesn't force
// every other invoice on the page to re-render (each of which recalculates
// its totals and, when printing/saving in sequence, would otherwise compound
// into very noticeable slowdowns as the batch size grows).
export default React.memo(JobInvoice);
